import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RouterModule } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

import { TranslateModule } from '@ngx-translate/core';

import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { UbiDialogModule, UberUiModule, UbiAppshellModule, UbiPaginationModule } from 'uber-ui';

import { AuthGuard } from './guards/auth.guards';
import { EnforcedMFAGuard } from './guards/enforced-mfa.guard';
import { LoggedGuard } from './guards/logged.guard';
import { ScreeningGuard } from './guards/screening.guards';
import { RiskAssessmentGuard } from './guards/risk-assessment.guards';
import { AdminRoleGuard } from './guards/admin-role.guards';
import { OnlyAdminRoleGuard } from './guards/only-admin-role.guards';

import { CustomerCommentCardComponent } from './components/customer-comment-card/customer-comment-card.component';
import { QuickScanCardComponent } from './components/quick-scan-card/quick-scan-card.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { ButtonToggleComponent } from './components/button-toggle/button-toggle.component';
import { ButtonToggleItemComponent } from './components/button-toggle/button-toggle-item/button-toggle-item.component';
import { CustomerOverviewCardComponent } from './components/customer-overview-card/customer-overview-card.component';
import { CustomerParticularsComponent } from './components/customer-particulars/customer-particulars.component';
import { TrDetailComponent } from './components/searches/components/searches-detail/tr-detail/tr-detail.component';
import { DowJonesDetailComponent } from './components/searches/components/searches-detail/dow-jones-detail/dow-jones-detail.component';
import { DowJones2DetailComponent } from './components/searches/components/searches-detail/dow-jones2-detail/dow-jones2-detail.component';
import { DowJones2Ver2DetailComponent } from './components/searches/components/searches-detail/dow-jones2-ver2-detail/dow-jones2-ver2-detail.component';
import { DowJones3DetailComponent } from './components/searches/components/searches-detail/dow-jones3-detail/dow-jones3-detail.component';
import { PopoverComponent } from './components/popover/popover.component';

import { DocumentUpdateComponent } from './components/document-update/document-update.component';
import { SearchesComponent } from './components/searches/searches.component';
import { SearchesDj2Component } from './components/searches/searches-dj2/searches-dj2.component';
import { SearchesDjComponent } from './components/searches/searches-dj/searches-dj.component';
import { SearchesInternetComponent } from './components/searches/searches-internet/searches-internet.component';
import { SearchesTrComponent } from './components/searches/searches-tr/searches-tr.component';
import { SearchesOwnNameComponent } from './components/searches/searches-own-name/searches-own-name.component';
import { SearchesArtemiscanComponent } from './components/searches/searches-artemiscan/searches-artemiscan.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { MergeDialogComponent } from 'modules/handshakes/components/merge-dialog/merge-dialog.component';
import {
  UpdateExistingCrpDialogComponent
} from 'modules/handshakes/components/update-existing-crp-dialog/update-existing-crp-dialog.component';
import { ChooseCrpDialogComponent } from 'modules/handshakes/components/choose-crp-dialog/choose-crp-dialog.component';
import { EnginesOptionComponent } from './components/engines-option/engines-option.component';
import { ExpiringDocumentTableComponent } from './components/expiring-document-table/expiring-document-table.component';
import { PeriodicReviewTableComponent } from './components/periodic-review-table/periodic-review-table.component';
import { UserNotificationsComponent } from './components/user-notifications/user-notifications.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { VersionCardComponent } from './components/version-card/version-card.component';

import { ThemeSwitcherDirective } from './directives/theme-switcher.directive';
import { MatchHeightDirective } from './directives/match-height.directive';
import { InfinityScrollDirective } from './directives/InfinityScroll.directive';
import { DropdownTogglerDirective } from './components/dropdown-menu/dropdown-toggler.directive';
import { EnginesOptionDynamicHeightDirective } from './components/engines-option/engines-option-dynamic-height.directive';
import {
  InternetSearchDetailComponent
} from './components/searches/components/searches-detail/internet-search-detail/internet-search-detail.component';
import {
  KeywordHighlighterDirective
} from './components/searches/components/searches-detail/internet-search-detail/keyword-highlighter.directive';
import { MapScaleValueDirective } from './components/searches/searches-internet/directive/map-scale-value.directive';
import {
  AddScreeningResultCommentDialogComponent
} from './components/searches/components/add-screening-result-comment-dialog/add-screening-result-comment-dialog.component';
import { NoCreditWarningDialogComponent } from './components/no-credit-warning-dialog/no-credit-warning-dialog.component';

import { NoCreditWarningDialogService } from './components/no-credit-warning-dialog/no-credit-warning-dialog.service';
import { CommentComponent } from './components/comment/comment.component';
import { SearchesDj3Component } from './components/searches/searches-dj3/searches-dj3.component';
import { IsArrayPipe, IsObjectPipe } from './services/instance-of.pipe';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const SEARCHES_COMPONENTS = [
  SearchesComponent,
  SearchesDj2Component,
  SearchesDjComponent,
  SearchesInternetComponent,
  SearchesTrComponent,
  SearchesOwnNameComponent,
  SearchesArtemiscanComponent,
];

const QUICKSCAN_COMPONENTS = [
  EnginesOptionComponent,
  QuickScanCardComponent,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    RouterModule,
    MatSelectModule,
    MatDialogModule,
    TranslateModule,

    UberUiModule,
    UbiAppshellModule,
    UbiPaginationModule,
    UbiDialogModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    MatSelectModule,
    MatDialogModule,
    ReactiveFormsModule,
    TranslateModule,

    UberUiModule,
    UbiAppshellModule,
    UbiPaginationModule,
    UbiDialogModule,

    CustomerCommentCardComponent,
    ButtonToggleComponent,
    DropdownMenuComponent,
    ButtonToggleItemComponent,
    DocumentUpdateComponent,
    BreadcrumbsComponent,
    CustomerOverviewCardComponent,
    LoadingSpinnerComponent,
    ProgressBarComponent,
    PopoverComponent,
    CustomerParticularsComponent,
    DialogComponent,
    TrDetailComponent,
    DowJonesDetailComponent,
    DowJones2DetailComponent,
    DowJones2Ver2DetailComponent,
    DowJones3DetailComponent,
    ExpiringDocumentTableComponent,
    PeriodicReviewTableComponent,
    UserNotificationsComponent,
    VersionCardComponent,
    CommentComponent,

    MatchHeightDirective,
    ThemeSwitcherDirective,
    DropdownTogglerDirective,
    InfinityScrollDirective,
    IsArrayPipe,
    IsObjectPipe,

    ...SEARCHES_COMPONENTS,
    ...QUICKSCAN_COMPONENTS
  ],
  providers: [
    AuthGuard,
    EnforcedMFAGuard,
    LoggedGuard,
    ScreeningGuard,
    RiskAssessmentGuard,
    AdminRoleGuard,
    OnlyAdminRoleGuard,

    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 20000 } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },

    NoCreditWarningDialogService
  ],
  declarations: [
    ...SEARCHES_COMPONENTS,
    ...QUICKSCAN_COMPONENTS,
    CustomerCommentCardComponent,
    DropdownMenuComponent,
    ButtonToggleComponent,
    ButtonToggleItemComponent,
    LoadingSpinnerComponent,
    ProgressBarComponent,
    PopoverComponent,
    DocumentUpdateComponent,
    CustomerOverviewCardComponent,
    CustomerParticularsComponent,
    DialogComponent,
    MergeDialogComponent,
    UpdateExistingCrpDialogComponent,
    ChooseCrpDialogComponent,
    BreadcrumbsComponent,
    TrDetailComponent,
    DowJonesDetailComponent,
    DowJones2DetailComponent,
    DowJones2Ver2DetailComponent,
    DowJones3DetailComponent,
    ExpiringDocumentTableComponent,
    PeriodicReviewTableComponent,
    UserNotificationsComponent,
    VersionCardComponent,

    MatchHeightDirective,
    InfinityScrollDirective,
    ThemeSwitcherDirective,
    DropdownTogglerDirective,
    EnginesOptionDynamicHeightDirective,
    InternetSearchDetailComponent,
    KeywordHighlighterDirective,
    MapScaleValueDirective,
    AddScreeningResultCommentDialogComponent,
    NoCreditWarningDialogComponent,
    CommentComponent,
    SearchesDj3Component,
    IsArrayPipe,
    IsObjectPipe

  ],
  entryComponents: [
    MergeDialogComponent,
    ChooseCrpDialogComponent,
    UpdateExistingCrpDialogComponent,
    TrDetailComponent,
    DowJonesDetailComponent,
    DowJones2DetailComponent,
    DowJones2Ver2DetailComponent,
    DowJones3DetailComponent,
    InternetSearchDetailComponent,
    AddScreeningResultCommentDialogComponent,
    NoCreditWarningDialogComponent
  ]
})
export class SharedModule { }
